'use client';

import { useState } from 'react';
import { Minus, Phone, RefreshCw, AlertTriangle, Bell, BellOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { toDialableBr } from '@/hooks/voice/phone-format';
import { WebphoneContacts, type ContactSource } from './webphone-contacts';
import type { WebphoneState, WebphoneError } from '@/hooks/voice/webphone-types';
import type { VoiceNotificationUiState } from '@/hooks/voice/use-incoming-call-notification';

type IdleTab = 'teclado' | ContactSource;
const TABS: { key: IdleTab; label: string }[] = [
  { key: 'teclado', label: 'Teclado' },
  { key: 'recentes', label: 'Recentes' },
  { key: 'clientes', label: 'Clientes' },
  { key: 'contatos', label: 'Contatos' },
];

interface Props {
  state: WebphoneState;
  error: WebphoneError | null;
  /** Ramal local registrado no SIP. null antes de registrar. */
  extension?: string | null;
  /** Nome do agente logado — vem do usuário Platform autenticado. */
  agentName?: string | null;
  /** Volta pra bubble (estado mais comum — fica acessível pra reabrir) */
  onMinimize: () => void;
  /**
   * Alça de arrasto do painel (26/08/2026): vai no CABEÇALHO, não no painel
   * inteiro — abaixo dele estão o teclado, as abas e o Ligar, e arrastar não
   * pode roubar o clique deles. Ausente = painel fixo (call sites antigos e
   * testes que renderizam o painel sozinho).
   */
  onDragHandlePointerDown?: (e: React.PointerEvent<HTMLElement>) => void;
  onDial: (number: string) => void;
  onSimulateIncoming: (number: string, name?: string) => void;
  onReconnect: () => void;
  /**
   * #435 — estado do aviso nativo de chamada. Ausente/'unsupported' esconde o
   * botao por completo.
   */
  notificationState?: VoiceNotificationUiState;
  /**
   * #435 — o clique AQUI e o gesto do agente que autoriza pedir a permissao.
   * Pedir de dentro de um efeito faz o Chrome bloquear em silencio, a permissao
   * virar `denied` permanente e ainda queimar a permissao do push do chat.
   */
  onToggleNotification?: () => void;
}

const KEYS = [
  ['1', '2', '3'],
  ['4', '5', '6'],
  ['7', '8', '9'],
  ['*', '0', '#'],
];

export function WebphonePanelIdle({
  state,
  error,
  extension,
  agentName,
  onMinimize,
  onDragHandlePointerDown,
  onDial,
  onSimulateIncoming,
  onReconnect,
  notificationState,
  onToggleNotification,
}: Props) {
  const [number, setNumber] = useState('');
  const [tab, setTab] = useState<IdleTab>('teclado');

  const append = (k: string) => setNumber((n) => n + k);
  const backspace = () => setNumber((n) => n.slice(0, -1));
  // Contato escolhido nas abas: telefone vem cru do ERP/chat → normaliza pro
  // formato da rota (0+DDD) e disca, igual ao click-to-call do cockpit.
  const handlePick = (phone: string) => {
    const n = toDialableBr(phone);
    if (n) onDial(n);
  };

  return (
    <div className="w-80 rounded-2xl bg-card border shadow-2xl overflow-hidden">
      {/* Header — nome do agente + ramal + status do registro. É também a alça
          de arrasto: o botão Minimizar que mora aqui dentro continua clicável
          (o hook de arrasto ignora pointerdown vindo de controle). */}
      <div
        onPointerDown={onDragHandlePointerDown}
        style={onDragHandlePointerDown ? { touchAction: 'none' } : undefined}
        title={onDragHandlePointerDown ? 'Arraste pra mover o webphone' : undefined}
        className={cn(
          'flex items-center justify-between px-4 py-3 border-b bg-muted/40',
          onDragHandlePointerDown && 'cursor-grab active:cursor-grabbing select-none',
        )}
      >
        <div className="flex items-center gap-2 min-w-0">
          <span
            className={cn(
              'h-2 w-2 rounded-full shrink-0',
              state === 'registered'
                ? 'bg-emerald-500'
                : state === 'connecting'
                  ? 'bg-amber-500 animate-pulse'
                  : 'bg-zinc-400',
            )}
            aria-label={
              state === 'registered'
                ? 'Pronto'
                : state === 'connecting'
                  ? 'Conectando'
                  : state === 'failed'
                    ? 'Falha'
                    : 'Desconectado'
            }
          />
          <div className="min-w-0">
            <p className="text-sm font-medium leading-tight truncate">
              {agentName ?? 'Webphone'}
            </p>
            <p className="text-[11px] text-muted-foreground leading-tight font-mono">
              {extension ? `Ramal ${extension}` : state === 'registered' ? 'sem ramal' : 'Conectando...'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-0.5">
          <Button
            size="icon"
            variant="ghost"
            onClick={onMinimize}
            className="h-7 w-7"
            aria-label="Minimizar"
            title="Minimizar"
          >
            <Minus className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Erro */}
      {error && (
        <div className="px-4 py-3 bg-amber-500/10 border-b text-xs flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-medium text-amber-700 dark:text-amber-400">
              {error.code === 'no_creds' ? 'Telefonia indisponível' : 'Erro'}
            </p>
            <p className="text-muted-foreground mt-0.5">{error.message}</p>
            <Button
              size="sm"
              variant="ghost"
              onClick={onReconnect}
              className="h-6 px-2 mt-1 text-xs"
            >
              <RefreshCw className="h-3 w-3 mr-1" /> Tentar novamente
            </Button>
          </div>
        </div>
      )}

      {/* Abas: Teclado + fontes de contato */}
      <div className="flex border-b text-xs">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={cn(
              'flex-1 py-2 font-medium transition-colors border-b-2 -mb-px',
              tab === t.key
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Altura fixa = a do teclado, pra não pular de tamanho ao trocar de aba */}
      <div className="h-[392px]">
      {tab === 'teclado' ? (
        <>
          {/* Display number */}
          <div className="px-4 py-4 border-b">
            <Input
              value={number}
              onChange={(e) => setNumber(e.target.value.replace(/[^0-9*#+]/g, ''))}
              placeholder="Digite o número"
              className="text-center text-xl font-mono h-12 border-0 focus-visible:ring-0"
            />
          </div>

          {/* Numpad */}
          <div className="grid grid-cols-3 gap-2 p-4">
            {KEYS.flat().map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => append(k)}
                disabled={state !== 'registered'}
                className={cn(
                  'h-12 rounded-lg font-medium transition-colors',
                  'bg-muted hover:bg-accent active:scale-95',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                )}
              >
                {k}
              </button>
            ))}
          </div>

          {/* Actions */}
          <div className="grid grid-cols-2 gap-2 px-4 pb-4">
            <Button
              variant="outline"
              onClick={backspace}
              disabled={!number}
              className="h-11"
            >
              ←
            </Button>
            <Button
              onClick={() => {
                if (number) {
                  onDial(number);
                  setNumber('');
                }
              }}
              disabled={state !== 'registered' || !number}
              className="h-11 bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              <Phone className="h-4 w-4 mr-2" /> Ligar
            </Button>
          </div>
        </>
      ) : (
        <WebphoneContacts
          source={tab}
          disabled={state !== 'registered'}
          onPick={handlePick}
        />
      )}
      </div>

      {/* #435 — aviso nativo do sistema pra chamada entrante com a aba em
          segundo plano. O clique daqui e o unico lugar de onde a permissao pode
          ser pedida: fora de gesto o Chrome bloqueia e queima a permissao. */}
      {notificationState && notificationState !== 'unsupported' && (
        <div className="border-t px-4 py-2 bg-muted/40">
          <Button
            size="sm"
            variant="ghost"
            onClick={onToggleNotification}
            disabled={notificationState === 'denied'}
            className="text-xs w-full justify-start"
            title={
              notificationState === 'denied'
                ? 'Desbloqueie as notificações deste site nas configurações do navegador'
                : 'Aviso do sistema quando entrar ligação com esta aba em segundo plano'
            }
          >
            {notificationState === 'on' ? (
              <Bell className="h-3 w-3 mr-1" />
            ) : (
              <BellOff className="h-3 w-3 mr-1" />
            )}
            {notificationState === 'on'
              ? 'Aviso de chamada ativado'
              : notificationState === 'denied'
                ? 'Aviso de chamada bloqueado pelo navegador'
                : 'Ativar aviso de chamada'}
          </Button>
        </div>
      )}

      {/* Dev: simular incoming pra testar UI sem motor. Opt-in via
          NEXT_PUBLIC_VOICE_MOCK=true (mesmo gate do MOCK_MODE do use-webphone)
          — escondido por padrão pra não vazar [DEV] no build do cliente. */}
      {process.env.NEXT_PUBLIC_VOICE_MOCK === 'true' && state === 'registered' && (
        <div className="border-t px-4 py-2 bg-muted/40">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onSimulateIncoming('5511988887777', 'Cliente Teste')}
            className="text-xs w-full justify-start"
          >
            <Bell className="h-3 w-3 mr-1" /> [DEV] Simular incoming
          </Button>
        </div>
      )}
    </div>
  );
}
